import {
  type Audiobook,
  type BookDetail,
  type BookEstimate,
  type BookPatch,
  buildPath,
  type RenderProgress,
  type StopMode,
} from "@contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { api } from "../../../api/fetcher";
import { queryKeys } from "../../../api/keys";
import { POLL_INTERVAL_MS } from "../../../api/poll";
import { useBookDetail } from "../bookContext";
import { type RenderConfirmation, renderIsActive } from "./logic";

/** BookLayout remains the only caller of GET /books/:id. Screen views refresh from its revision. */
export function useEstimate() {
  const book = useBookDetail();
  const client = useQueryClient();
  // biome-ignore lint/correctness/useExhaustiveDependencies: the Book revision deliberately refreshes this derived view.
  useEffect(() => {
    void client.invalidateQueries({ queryKey: queryKeys.estimate(book.id) });
  }, [book.id, book.updatedAt, client]);
  return useQuery({
    queryKey: queryKeys.estimate(book.id),
    queryFn: ({ signal }) =>
      api<BookEstimate>(buildPath("/books/:id/estimate", { id: book.id }), { signal }),
  });
}

export function useRenderSettings() {
  const book = useBookDetail();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (patch: Pick<BookPatch, "ttsModel" | "stability">) =>
      api<BookDetail>(buildPath("/books/:id", { id: book.id }), { method: "PATCH", body: patch }),
    retry: false,
    onSuccess: (updated) => client.setQueryData(queryKeys.book(book.id), updated),
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.book(book.id) }),
        client.invalidateQueries({ queryKey: queryKeys.estimate(book.id) }),
      ]);
    },
  });
}

export function useStartRender() {
  const book = useBookDetail();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (confirmation: RenderConfirmation) =>
      confirmation.chapter
        ? api<RenderProgress>(
            buildPath("/books/:id/chapters/:cid/render", {
              id: book.id,
              cid: confirmation.chapter.chapterId,
            }),
            {
              method: "POST",
              body: {
                estimateFingerprint: confirmation.estimate.fingerprint,
                confirmReRender: confirmation.reRenderChapterIds.length > 0,
              },
            },
          )
        : api<RenderProgress>(buildPath("/books/:id/render", { id: book.id }), {
            method: "POST",
            body: {
              fingerprint: confirmation.estimate.fingerprint,
              confirmReRenderChapterIds: confirmation.reRenderChapterIds,
            },
          }),
    // A refused/stale/uncertain paid action always needs another explicit click.
    retry: false,
    onSuccess: (progress) => client.setQueryData(queryKeys.renderProgress(book.id), progress),
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.books }),
        client.invalidateQueries({ queryKey: queryKeys.estimate(book.id) }),
      ]);
    },
  });
}

export function useRenderProgress() {
  const book = useBookDetail();
  const client = useQueryClient();
  // biome-ignore lint/correctness/useExhaustiveDependencies: the Book revision deliberately refreshes this derived view.
  useEffect(() => {
    void client.invalidateQueries({ queryKey: queryKeys.renderProgress(book.id) });
  }, [book.id, book.updatedAt, client]);
  return useQuery({
    queryKey: queryKeys.renderProgress(book.id),
    queryFn: ({ signal }) =>
      api<RenderProgress>(buildPath("/books/:id/render", { id: book.id }), { signal }),
    refetchInterval: (query) =>
      renderIsActive(query.state.data?.status) ? POLL_INTERVAL_MS : false,
  });
}

export function useStopRender() {
  const book = useBookDetail();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (mode: StopMode) =>
      api<RenderProgress>(buildPath("/books/:id/render/stop", { id: book.id }), {
        method: "POST",
        body: { mode },
      }),
    retry: false,
    onSuccess: (progress) => client.setQueryData(queryKeys.renderProgress(book.id), progress),
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.books }),
  });
}

export function useAudiobook() {
  const book = useBookDetail();
  const client = useQueryClient();
  // biome-ignore lint/correctness/useExhaustiveDependencies: the Book revision deliberately refreshes this derived view.
  useEffect(() => {
    void client.invalidateQueries({ queryKey: queryKeys.audiobook(book.id) });
  }, [book.id, book.updatedAt, book.audiobook?.id, client]);
  return useQuery({
    queryKey: queryKeys.audiobook(book.id),
    queryFn: ({ signal }) =>
      api<Audiobook>(buildPath("/books/:id/audiobook", { id: book.id }), { signal }),
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.assembling || renderIsActive(book.render?.status)
        ? POLL_INTERVAL_MS
        : false,
  });
}
